// src/apolloClient.js
import { ApolloClient, InMemoryCache, HttpLink, from } from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { setContext } from '@apollo/client/link/context';
import { split } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { RetryLink } from '@apollo/client/link/retry';
import { onError } from '@apollo/client/link/error';
import * as Sentry from '@sentry/react';

// Assuming 'rangeHeadlessApi' is globally available or imported correctly
const { apiEndpoint } = rangeHeadlessApi || { apiEndpoint: 'YOUR_FALLBACK_ENDPOINT' };
const GRAPHQL_ENDPOINT = apiEndpoint;

// Global store for retailers data (populated by ConfigContext)
// This allows Apollo to look up keyIndex for multi-key support
window.rangeHeadlessRetailers = window.rangeHeadlessRetailers || {};

// --- Error Link for Sentry Integration ---
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    const { operationName, variables, query } = operation;
    
    // Only capture critical errors to avoid noise
    const isCriticalError = networkError || 
        (graphQLErrors && graphQLErrors.some(error => 
            error.extensions?.code === 'INTERNAL_ERROR' || 
            error.message?.includes('Load failed') ||
            error.message?.includes('Network error')
        ));

    if (isCriticalError) {
        Sentry.withScope((scope) => {
            scope.setLevel('error');
            scope.setTag('error_type', 'apollo_graphql_error');
            scope.setTag('operation_name', operationName || 'unknown');
            
            // Add operation context
            scope.setContext('apollo_operation', {
                operationName,
                operationType: operation.query?.definitions?.[0]?.operation || 'unknown',
                variables: variables ? JSON.stringify(variables, null, 2) : 'none',
                queryString: query ? query.loc?.source?.body || 'query not available' : 'no query'
            });

            // Add network error details
            if (networkError) {
                scope.setContext('network_error', {
                    name: networkError.name,
                    message: networkError.message,
                    statusCode: networkError.statusCode,
                    bodyText: networkError.bodyText,
                    stack: networkError.stack
                });
                
                scope.setTag('network_error_status', networkError.statusCode || 'unknown');
                scope.setFingerprint([
                    'apollo_network_error',
                    operationName || 'unknown_operation',
                    networkError.statusCode || 'no_status'
                ]);
            }

            // Add GraphQL error details
            if (graphQLErrors && graphQLErrors.length > 0) {
                scope.setContext('graphql_errors', {
                    errors: graphQLErrors.map(error => ({
                        message: error.message,
                        locations: error.locations,
                        path: error.path,
                        extensions: error.extensions
                    }))
                });
                
                const primaryError = graphQLErrors[0];
                scope.setTag('graphql_error_code', primaryError.extensions?.code || 'unknown');
                scope.setFingerprint([
                    'apollo_graphql_error',
                    operationName || 'unknown_operation',
                    primaryError.extensions?.code || primaryError.message?.slice(0, 50)
                ]);
            }

            // Capture the error
            const errorToCapture = networkError || new Error(`GraphQL Error in ${operationName}: ${graphQLErrors?.[0]?.message}`);
            Sentry.captureException(errorToCapture);
        });
    }

    // Log to console for debugging
    if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) =>
            console.error(`GraphQL error in ${operationName}:`, { message, locations, path })
        );
    }

    if (networkError) {
        console.error(`Network error in ${operationName}:`, networkError);
    }
});

// --- HTTP Link ---
// const httpLink = new HttpLink({
//     uri: GRAPHQL_ENDPOINT,
// });
const httpLink = new BatchHttpLink({
    uri: `${GRAPHQL_ENDPOINT}/batch`, // Use the batch endpoint
    batchMax: 10, // Max number of operations to batch
    batchInterval: 50, // Wait 20ms to batch operations
    batchDebounce: true, // Extend the batch interval if new operations come in
    batchKey: () => "single-batch",
    batchHandler: (operations, forward) => {
        console.log(`[Batching] ${operations.length} operations:`, 
            operations.map(op => op.operationName));
        
        // Default batch handler
        return forward(operations);
    }
});

const regularHttpLink = new HttpLink({
    uri: GRAPHQL_ENDPOINT,
});

const splitLink = split(
    ({ query }) => {
        const definition = getMainDefinition(query);
        // Use regular link for mutations and subscriptions
        return (
            definition.kind === 'OperationDefinition' &&
            definition.operation === 'mutation'
        );
    },
    regularHttpLink, // Mutations go here
    httpLink // Queries go here (batched)
);

// --- Retry Link with Enhanced Logging ---
const retryLink = new RetryLink({
    delay: {
        initial: 300,
        max: Infinity,
        jitter: true
    },
    attempts: {
        max: 3,
        retryIf: (error, operation) => {
            const { operationName } = operation;
            
            // Don't retry on 429 (rate limit) errors
            if (error?.statusCode === 429) return false;

            // Don't retry on 4xx errors except 429
            if (error?.statusCode >= 400 && error?.statusCode < 500) return false;

            // Log retry attempts for critical operations
            if (operationName && ['menu', 'checkout', 'product'].some(op => 
                operationName.toLowerCase().includes(op))) {
                console.warn(`Retrying critical operation: ${operationName}`, {
                    error: error?.message,
                    statusCode: error?.statusCode
                });
            }

            // Retry on network errors and 5xx errors
            return !!error;
        }
    }
});

// --- Context Link (for special headers) ---
const contextLink = setContext((graphqlRequest, { headers }) => {
    const newHeaders = { ...headers };

    // Multi-key support: Add x-rh-keyindex header based on retailerId
    const retailerId = graphqlRequest.variables?.retailerId;
    if (retailerId && window.rangeHeadlessRetailers && window.rangeHeadlessRetailers[retailerId]) {
        const retailer = window.rangeHeadlessRetailers[retailerId];
        if (retailer.keyIndex !== undefined && retailer.keyIndex !== null) {
            newHeaders['x-rh-keyindex'] = String(retailer.keyIndex);
            console.log(`🔑 Apollo: Adding keyIndex=${retailer.keyIndex} for retailer ${retailerId} (${operationName})`);
        }
    } else if (retailerId) {
        console.warn(`⚠️ Apollo: No keyIndex found for retailer ${retailerId} (${operationName})`, {
            hasRetailers: !!window.rangeHeadlessRetailers,
            retailersCount: window.rangeHeadlessRetailers ? Object.keys(window.rangeHeadlessRetailers).length : 0
        });
    }

    // Add x-rh-order header for order queries
    const operationName = graphqlRequest.operationName;
    if (operationName === 'OrdersByOrderNumber') {
        newHeaders['x-rh-order'] = 'true';
    }

    return { headers: newHeaders };
});

const link = from([
    errorLink,       // First, handle errors and send to Sentry
    retryLink,       // Then retry logic
    contextLink,     // Then add headers
    splitLink         // Finally make the request
]);

// --- Apollo Client Cache Configuration ---
const cache = new InMemoryCache({
    typePolicies: {
        Query: {
            fields: {
                // Menu queries - network-only for critical real-time data
                menu: {
                    keyArgs: ["retailerId", "filter", "menuType", "sort"],
                    merge(existing, incoming, { args, variables }) {
                        if (!incoming) return existing || {};

                        const merged = { ...(existing || {}) };
                        if (incoming.__typename) merged.__typename = incoming.__typename;

                        const queryName = variables?.queryName || (args?.operation?.name?.value) || '';
                        const isSpecialsQuery = queryName === 'MenuBySpecialsSection';

                        if (isSpecialsQuery) {
                            if (incoming.products) {
                                merged.productsCount = incoming.products.length;
                            } else if (!existing?.productsCount && incoming.products === null) {
                                merged.productsCount = 0;
                            }
                        } else if (typeof incoming.productsCount === 'number') {
                            merged.productsCount = incoming.productsCount;
                        } else if (existing && typeof existing.productsCount === 'number') {
                            merged.productsCount = existing.productsCount;
                        }

                        if (incoming.brands) merged.brands = incoming.brands;
                        if (incoming.weights) merged.weights = incoming.weights;
                        if (incoming.tags) merged.tags = incoming.tags;

                        const existingProducts = existing?.products ? existing.products.slice(0) : [];
                        const incomingProducts = incoming?.products || [];

                        if (incomingProducts.length > 0) {
                            if (args?.pagination?.offset != null && args.pagination.offset > 0) {
                                let newProductList = existingProducts.slice(0);
                                const requiredLength = args.pagination.offset + incomingProducts.length;
                                while (newProductList.length < requiredLength) {
                                    newProductList.push(undefined);
                                }
                                for (let i = 0; i < incomingProducts.length; ++i) {
                                    newProductList[args.pagination.offset + i] = incomingProducts[i];
                                }
                                merged.products = newProductList.filter(p => p !== undefined);
                            } else {
                                merged.products = incomingProducts;
                            }
                        } else if (args?.pagination?.offset === 0 && incomingProducts.length === 0) {
                            merged.products = [];
                        } else if (!existing?.products) {
                            merged.products = [];
                        }
                        return merged;
                    },
                },

                // Checkout queries
                checkout: {
                    keyArgs: ["id"],
                    merge(existing, incoming) {
                        // Always prefer incoming data for checkout
                        return incoming;
                    },
                    read(existing, { args, toReference }) {
                        if (existing) return existing;
                        // Try to read from normalized cache
                        if (args?.id) {
                            return toReference({
                                __typename: 'Checkout',
                                id: args.id
                            });
                        }
                        return existing;
                    }
                },

                // Product queries - cache-first with short TTL
                product: {
                    keyArgs: ["id", "retailerId"],
                    merge: true
                },

                // Specials - network-only (time-sensitive)
                specials: {
                    keyArgs: ["retailerId"],
                    read() {
                        return undefined; // Force network fetch
                    }
                },

                // Retailer info - cache-and-network (changes infrequently)
                retailer: {
                    keyArgs: ["id"],
                    merge: true
                },

                // Order
                orders: {
                    keyArgs: ["retailerId", "orderNumber"],
                    merge(existing, incoming) {
                        return incoming || existing;
                    }
                }
            }
        },
        // Checkout/Cart - optimistic updates with cache-first
        Checkout: {
            keyFields: ["id"],
            fields: {
                items: {
                    merge(existing = [], incoming) {
                        return incoming;
                    }
                }
            }
        },

        CheckoutItem: {
            keyFields: ["id"],
            fields: {
                // Ensure product references are properly cached
                product: {
                    merge: true
                }
            }
        },

        Product: {
            keyFields: ["id"],
        },
        Brand: {
            keyFields: ["id"],
        },
        ProductVariant: {
            keyFields: ["id"]
        },
        Order: {
            keyFields: ["id"],
        }
    }
});

// Configure default fetch policies
const client = new ApolloClient({
    link: link,
    cache: cache,
    connectToDevTools: process.env.NODE_ENV === 'development',
    defaultOptions: {
        watchQuery: {
            fetchPolicy: 'cache-and-network',
            nextFetchPolicy: 'cache-first',
            errorPolicy: 'all',
            notifyOnNetworkStatusChange: true,
        },
        query: {
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
            notifyOnNetworkStatusChange: true,
        },
        mutate: {
            errorPolicy: 'all',
        }
    }
});

export default client;
